import axios from 'axios'
import toast from 'react-hot-toast'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          })

          if (response.data.success) {
            const { token, refreshToken: newRefreshToken } = response.data.data
            localStorage.setItem('token', token)
            localStorage.setItem('refreshToken', newRefreshToken)

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        // Redirect to login
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || error.message || 'Wystąpił błąd'
    
    // Don't show toast for certain errors
    const silentErrors = [401, 403]
    if (!silentErrors.includes(error.response?.status)) {
      toast.error(errorMessage)
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  refreshToken: (data) => api.post('/auth/refresh', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  checkEmailAvailability: (email) => api.get(`/auth/check-email?email=${email}`),
  getUserStats: () => api.get('/auth/stats'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
}

// Documents API
export const documentsAPI = {
  getDocuments: (params = {}) => api.get('/documents', { params }),
  getDocument: (id) => api.get(`/documents/${id}`),
  uploadDocument: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (id, data) => api.put(`/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/documents/${id}`),
  downloadDocument: (id) => api.get(`/documents/${id}/download`, {
    responseType: 'blob'
  }),
  reprocessOCR: (id, data) => api.post(`/documents/${id}/reprocess`, data),
  getDocumentStats: () => api.get('/documents/stats'),
  confirmReceipt: (id, data) => api.put(`/documents/${id}/confirm-receipt`, data),
  confirmReceiptForce: (id, data) => api.put(`/documents/${id}/confirm-receipt?force=true`, data),
}

// Notifications API
export const notificationsAPI = {
  getNotifications: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
}

// Categories API
export const categoriesAPI = {
  getCategories: (params = {}) => api.get('/categories', { params }),
  getCategory: (id) => api.get(`/categories/${id}`),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  reorderCategories: (data) => api.put('/categories/reorder', data),
  createDefaultCategories: () => api.post('/categories/default'),
  getCategoryStats: (params = {}) => api.get('/categories/stats', { params }),
}

// Budget API
export const budgetAPI = {
  // Expenses
  getExpenses: (params = {}) => api.get('/budget/expenses', { params }),
  getExpense: (id) => api.get(`/budget/expenses/${id}`),
  createExpense: (data) => api.post('/budget/expenses', data),
  updateExpense: (id, data) => api.put(`/budget/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/budget/expenses/${id}`),
  
  // Incomes
  getIncomes: (params = {}) => api.get('/budget/incomes', { params }),
  getIncome: (id) => api.get(`/budget/incomes/${id}`),
  createIncome: (data) => api.post('/budget/incomes', data),
  updateIncome: (id, data) => api.put(`/budget/incomes/${id}`, data),
  deleteIncome: (id) => api.delete(`/budget/incomes/${id}`),

  // Budgets
  getBudgets: (params = {}) => api.get('/budget/budgets', { params }),
  getBudget: (id) => api.get(`/budget/budgets/${id}`),
  createBudget: (data) => api.post('/budget/budgets', data),
  updateBudget: (id, data) => api.put(`/budget/budgets/${id}`, data),
  deleteBudget: (id) => api.delete(`/budget/budgets/${id}`),
  
  // Goals
  getGoals: (params = {}) => api.get('/budget/goals', { params }),
  getGoal: (id) => api.get(`/budget/goals/${id}`),
  createGoal: (data) => api.post('/budget/goals', data),
  updateGoal: (id, data) => api.put(`/budget/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/budget/goals/${id}`),
  contributeToGoal: (id, data) => api.post(`/budget/goals/${id}/contribute`, data),

  // Reports
  getExpenseReport: (params = {}) => api.get('/budget/reports/expenses', { params }),
  getBudgetAlerts: () => api.get('/budget/alerts'),
}

// OCR API
export const ocrAPI = {
  getOCRStatus: () => api.get('/ocr/status'),
  processDocument: (id, data) => api.post(`/ocr/process/${id}`, data),
  reprocessDocument: (id, data) => api.post(`/ocr/reprocess/${id}`, data),
  getProcessingStatus: (processingId) => api.get(`/ocr/processing/${processingId}`),
  getOCRResults: (id) => api.get(`/ocr/results/${id}`),
  getOCREngines: () => api.get('/ocr/engines'),
  categorizeDocument: (id, data) => api.post(`/ocr/categorize/${id}`, data),
  processBatch: (data) => api.post('/ocr/batch', data),
  analyzeDocument: (id, data) => api.post(`/ocr/analyze/${id}`, data),
  getOCRStats: () => api.get('/ocr/stats'),
}

// Dashboard API
export const dashboardAPI = {
  getDashboard: () => api.get('/dashboard'),
  getCashflow: (params = {}) => api.get('/dashboard/cashflow', { params }),
  getExpenseStats: (params = {}) => api.get('/dashboard/expenses', { params }),
  getDocumentStats: () => api.get('/dashboard/documents'),
  getBudgetStats: () => api.get('/dashboard/budgets'),
  getActivity: (params = {}) => api.get('/dashboard/activity', { params }),
}

// Search API
export const searchAPI = {
  search: (q, limit) => api.get('/search', { params: { q, limit } }),
}

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
  info: () => api.get('/info'),
}

export const adminAPI = {
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
}

export default api
